import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";

import api from "@/shared/api";
import { Data } from "@/shared/types.ts";

export type PriceAdjustment = {
  id: number;
  createdAt: string;
  title: string;
  type: "inc_1" | "dec_1";
  category: string;
  price: number;
  createdBy: string;
  reason: string;
};

export type PriceBreakdownPosition = {
  title: string;
  amount: number;
};

export type OrderPriceBreakdown = {
  basePrice: number;
  positions: PriceBreakdownPosition[];
  adjustments: PriceAdjustment[];
  adjustmentsTotal: number;
  totalPrice: number;
  paidAmount: number;
  toPay: number;
  hasActivePayment: boolean;
};

/** Описание ошибки из ответа core-service (guard-ошибки приходят человекочитаемыми). */
export async function priceErrorDescription(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.clone().json()) as {
        description?: string;
      };

      if (body.description) return body.description;
    } catch {
      /* не JSON — вернём общий текст */
    }
  }

  return "Что-то пошло не так";
}

export function useOrderPrice({ orderId }: { orderId: string | number }) {
  return useQuery<Data<OrderPriceBreakdown>>({
    queryKey: ["orders", "price", orderId],
    queryFn: () =>
      api
        .get<Data<OrderPriceBreakdown>>(`/api/v1/orders/admin/${orderId}/price`)
        .json(),
  });
}

export function useAddPriceAdjustment({
  orderId,
}: {
  orderId: string | number;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Data<OrderPriceBreakdown>,
    Error,
    { amount: number; reason: string }
  >({
    mutationFn: (data) =>
      api
        .post(`/api/v1/orders/admin/${orderId}/price/adjustments`, {
          json: data,
        })
        .json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "price", orderId] });
    },
  });
}

export function useDeletePriceAdjustment({
  orderId,
}: {
  orderId: string | number;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Data<OrderPriceBreakdown>,
    Error,
    { adjustmentId: number }
  >({
    mutationFn: ({ adjustmentId }) =>
      api
        .delete(
          `/api/v1/orders/admin/${orderId}/price/adjustments/${adjustmentId}`,
        )
        .json(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orders", "price", orderId],
      });
    },
  });
}
